/**
 * @module common/components/form_fields
 */
import React, { Component, PropTypes } from 'react';
import classnames from 'classnames';
import { v4 } from 'node-uuid';
import { Field } from 'redux-form';
import { PWD_SCORE_LABELS } from 'common/variables';
import Select from 'react-virtualized-select';
import Textarea from 'react-textarea-autosize';
import FileSelector from 'common/components/file_selector';
import StarRating from 'common/components/star_rating';
import DatePicker from 'react-datepicker';
import ColorPicker from 'common/components/color_picker';
import { pwdStrengthMeter, removeExtraSpaces } from 'common/utils';
import moment from 'moment';
import omit from 'lodash/omit';

export const DEFAULT_REQUIRED_MESSAGE = 'Please enter a value.';
export const DEFAULT_MIN_LENGTH_MESSAGE = (minLength) => (
  `This field must have at least ${minLength} characters.`
);
export const EMAIL_INVALID_MESSAGE = 'Please enter a valid email.';
export const PHONE_INVALID_MESSAGE = 'Please enter a valid phone number.';
export const URL_INVALID_MESSAGE = 'Please enter a valid URL.';
export const PHONE_PLACEHOLDER = '+1.877.887.7442';
export const REQUIRED_COLOR_MESSAGE = 'Please pick a color.';

const FieldPropTypes = {
  input: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.array,
  meta: PropTypes.object,
};

const FormGroup = (props) => {
  const { field, children } = props;
  let classes = classnames('form-group', field.formClasses || '',
    { error: field.meta.touched && field.meta.invalid }
  );

  return <div className={classes}>{ children }</div>;
};

/**
 * Form input component.
 *
 * @author Kamil Grymuza <kamil@startupfoundry.com>
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 *
 * @returns {void}
 */
const InputField = ({ field, type }) => {
  const hasError = field.meta.touched && field.meta.invalid;
  let value = field.input.value || field.defaultValue;
  let hasValue = !!value;
  if (type === 'number') {
    if (field.input.value || field.input.value === 0) {
      value = field.input.value;
      hasValue = true;
    } else if (field.defaultValue || field.defaultValue === 0) {
      value = field.defaultValue;
      hasValue = true;
    }
  }
  const classes = classnames('form-control', field.classes || '', {
    filled: hasValue,
    invalid: field.meta.touched && field.meta.invalid && field.meta.value,
    'with-pre-value': field.preValue,
    'with-post-value': field.postValue
  });

  return (
    <FormGroup field={field}>
      { field.preValue && <div className="pre-value">{ field.preValue }</div> }
      <input
        {...field.input}
        disabled={field.disabled}
        className={classes}
        id={`field-id-${field.input.name}`}
        type={type}
        value={hasValue? value : ''}
        placeholder={field.placeholder}
      />
      { field.postValue && <div className="post-value">{ field.postValue }</div> }
      <span className="bar" />
      <label className="form-label animate" htmlFor={`field-id-${field.input.name}`}>
        <span>
          { field.label }
          { !!field.labelDescription &&
          <span className="show-for-large label-description">
            ({ field.labelDescription })
          </span> }
        </span>

        {field.verified === true &&
          <span className="label label-success">
            <i className="fa fa-check-circle" /> Verified
          </span>
        }

        {field.verified === false &&
          <span className="label label-warning">
            <i className="fa fa-exclamation-triangle" /> Pending
          </span>
        }
      </label>
      { hasError && <span className="error-msg">{field.meta.error}</span> }
    </FormGroup>
  );
};

export const TextInputField = field => <InputField field={field} type="text" />;
export const EmailInputField = field => <InputField field={field} type="email" />;
export const DateInputField = field => <InputField field={field} type="date" />;
export const NumberInputField = field => <InputField field={field} type="number" />;
export const MoneyInputField = field => (
  <InputField field={{
    ...field,
    input: {
      ...field.input,
      /* make sure value has at most 2 decimal places */
      onChange: (e) => {
        const raw = e.target.value || "0";
        const parts = raw.split('.');
        const decimals = parts.length > 1 ? parts[1] : 0;
        const rounded = raw.endsWith('.') || decimals.length < 2
          ? raw
          : parseFloat(Number(raw).toFixed(2));
        field.input.onChange(rounded);
      },
      step: field.step || 1
    },
    classes: (field.classes || []).concat("money")
  }} type="number" />
);
export const PhoneInputField = field => (
  <InputField type="phone" field={{
    ...field,
    placeholder: PHONE_PLACEHOLDER,
    labelDescription: `Be sure to include + and your country code. Example: ${PHONE_PLACEHOLDER}`
  }} />
);

/**
 * Simplest form input field without label or any other additional functionality.
 * This component can have a ref defined, which can be set by providing
 * a props.getRef function.
 *
 * This can't be a stateless function since we need to use it with `withRef`
 *
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
// prefer-stateless-function
class SimpleTextInputField extends Component {
  render() {
    const { input, placeholder, disabled, name } = this.props;
    const getRef = this.props.getRef ? element => this.props.getRef(element) : null;

    return (
      <div className="simple-input">
        <input
          {...input}
          placeholder={placeholder}
          disabled={disabled}
          id={`field-id-${name}`}
          type="text"
          ref={getRef}
        />
      </div>
    );
  }
}

/**
 * PropTypes.
 *
 * @property {object} input - Field input object
 * @property {string} placeholder - placeholder text
 * @property {bool} disabled - boolean defining if field is active or not
 * @property {string} name - field name
 * @property {func} getRef - function returning a ref handler for this element
 */
SimpleTextInputField.propTypes = {
  input: PropTypes.object,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  getRef: PropTypes.func,
};

/**
 * Password input field, with optionals strength indicator and visibility toggle.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
export class PasswordInputField extends Component {
  constructor(props) {
    super(props)
    this.state = {visible: false}
    this.toggleVisibility = this.toggleVisibility.bind(this)
  }

  toggleVisibility() {
    this.setState({visible: !this.state.visible})
  }

  render() {
    const field = this.props;
    const score = field.indicateStrength ? pwdStrengthMeter(field.input.value).score : null;
    const { visible } = this.state

    return (
      <div className="password-input">
        { field.showVisibilityToggle &&
        <i className={"fa fa-eye" + (visible? " active":"")} onClick={this.toggleVisibility}></i> }
        <InputField field={field} type={visible? "text":"password"} />
        { field.indicateStrength && field.input.value &&
          <div className={`strength-indicator score-${score}`}>{PWD_SCORE_LABELS[score]}</div>}
      </div>
    );
  }
};

/**
 * Custom select field. It can display array of elements as a list of options.
 * Accepts either a simple array:
 * [
 *   { field: 'foo', label: 'foo' }, { field: 'bar', label: 'Bar' }
 * ]
 *
 * or array of two elements :
 * [
 *   [{ field: 'foo', label: 'foo' }, { field: 'bar', label: 'Bar' }]
 * ]
 *
 * If `customOnChange` is provided, it will be called when option is selected,
 * but the built-in `onChange` is still called.
 *
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 *
 * @returns {void}
 */
const SelectField = (field) => {
  const hasError = field.meta.touched && field.meta.invalid;
  const options = [];
  const optionsValues = [];
  const groups = {};

  /* Add extra option in case empty value is allowed */
  if (field.allowEmpty) {
    options.push(
      <option value="" key={v4()}>{ field.emptyLabel || "Select" } </option>,
    )
  }

  field.options.forEach((optionSet) => {
    /* options provided in pairs */
    if (optionSet[0]) {
      options.push(
        <option value={optionSet[0].field} key={v4()} disabled={optionSet[0].disabled}>
          {optionSet[0].label}
        </option>,
        <option value={optionSet[1].field} key={v4()} disabled={optionSet[1].disabled}>
          {optionSet[1].label}
        </option>
      );
      optionsValues.push(optionSet[0].field, optionSet[1].field);
    /* options provided individually */
    } else {
      const option = (
        <option value={optionSet.field} key={v4()} disabled={optionSet.disabled}>
          {optionSet.label}
        </option>
      );
      const groupId = optionSet.group || optionSet.field;

      groups[groupId] = (groups[groupId] || []).concat(option);

      optionsValues.push(optionSet.field, optionSet.field);
    }
  });

  return (
    <FormGroup field={{...field, formClasses: "select-wrapper " + (field.formClasses || '')}}>
      <select
        className="form-control"
        autoComplete="off"
        required={true} // just so that it's blurred if an empty value is selected
        onChange={(e) => {
          const selectedValue = optionsValues[e.target.selectedIndex];

          field.input.onChange(e);
          if (field.customOnChange) {
            field.customOnChange(selectedValue);
          }
        }}
        value={field.input.value}
        id={`field-id-${field.input.name}`}
        disabled={field.disabled}
      >
        { options }
        { Object.keys(groups).map((group, i) => (
        groups[group].length === 1 && groups[group][0].props.value == group?
        groups[group][0] :
        <optgroup label={group} key={i}>
          { groups[group] }
        </optgroup>
        )) }
      </select>
      { field.label &&
        <label className="form-label animate" htmlFor={`field-id-${field.input.name}`}>
          {field.label}
        </label>
      }
      { hasError && <span className="error-msg">{field.meta.error}</span> }
    </FormGroup>
  );
};

/**
 * Simple textarea form field.
 *
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 *
 * @returns {void}
 */
const TextAreaField = ({ input, classes, meta: { touched, error, invalid, valid }, label,
                         description, disabled, rows, minCharCount, maxCharCount, placeholder }) => {

  const charCount = input.value? removeExtraSpaces(input.value).length : 0;
  let charCountError = null;
  /* char count lower than minimum */
  if (minCharCount && charCount && charCount < minCharCount) {
    charCountError = charCount;
  /* char count higher than maximum */
  } else if (maxCharCount && charCount > maxCharCount) {
    charCountError = maxCharCount - charCount;
  }

  return (<FormGroup field={{meta: {touched, error, invalid}, formClasses: "textarea"}}>
    {label &&
    <label className="form-label" htmlFor={`field-id-${input.name}`}>
      { label }
    </label>}

    { description && <p className="description">{description}</p> }
    <textarea
      className={classnames((classes || 'form-control'), {
        empty: !input.value
      })}
      {...input}
      rows={rows || 1}
      disabled={disabled}
      placeholder={placeholder}
    />
    { touched && error && <div className="error-msg">{error}</div> }
    { charCountError &&
    <div className="char-counters">
      <span className="text-danger">{ charCountError }</span>
    </div> }
  </FormGroup>);
};

TextAreaField.propTypes = {
  ...FieldPropTypes,
  classes: PropTypes.string,
};



/**
 * Simple textarea form field.
 *
 * @author Tyler Travitz
 *
 * @returns {void}
 */
const BasicTextAreaField = ({ input, classes, meta: { touched, error, invalid, valid }, label,
                         description, formClasses, minRows, disabled}) => {
  return (
    <FormGroup field={{meta: {touched, error, invalid}, formClasses}}>
      {label &&
      <label className="form-label" htmlFor={`field-id-${input.name}`}>
        { label }
      </label>}
      { description && <p className="description">{description}</p> }
      <textarea
        className={classnames((classes || 'form-control'), {
          empty: !input.value
        })}
        {...input}
        rows={minRows || 1}
        disabled={disabled}
      />
      { touched && error && <div className="error-msg">{error}</div> }
    </FormGroup>
  );
};

TextAreaField.propTypes = {
  ...FieldPropTypes,
  classes: PropTypes.string,
};


/**
 * Resizable textarea form field.
 *
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 * @author Kamil Grymuza <kamil@crowdspring.com>
 *
 * @returns {void}
 */
const ResizableTextAreaField = ({
  input, classes, rows, meta: { touched, error, invalid, valid }, label, description, getRef,
  placeholder
}) => {
  return (<FormGroup field={{meta: {touched, error, invalid}}}>
    { description && <p className="description">{description}</p> }
    <Textarea
      className={classnames((classes || 'form-control'), {
        empty: !input.value
      })}
      spellCheck={false}
      minRows={rows}
      inputRef={getRef}
      placeholder={placeholder}
      {...input}
    />
    {label &&
    <label className="form-label animate" htmlFor={`field-id-${input.name}`}>
      { label }
    </label>}
    { touched && error && <span className="error-msg">{error}</span> }
  </FormGroup>);
};

TextAreaField.propTypes = {
  ...FieldPropTypes,
  classes: PropTypes.string,
};


/**
 * Custom checkbox field with a label and error handling. When `customOnChange`
 * is provided it will be called after the built-in `onChange`.
 *
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 *
 * @returns {void}
 */
const CheckBoxField = (field) => {
  const hasError = field.meta.touched && field.meta.invalid;
  const classes = classnames('checkbox form-group', {
    error: hasError,
    disabled: field.disabled
  });

  return (
    <div className={classes}>
      <input
        id={`field-id-${field.input.name}`}
        type="checkbox"
        className="form-control"
        onChange={() => {
          const checkedValue = !field.input.value;
          field.input.onChange(checkedValue);

          if (field.customOnChange) {
            field.customOnChange(checkedValue);
          }
        }}
        checked={field.input.value}
        disabled={field.disabled}
      />
      <label
        htmlFor={`field-id-${field.input.name}`}
        className="form-label">
        { field.label }
      </label>

      { hasError && <span className="error-msg">{field.meta.error}</span> }

      { field.description && <p className="description">{field.description}</p> }

      { field.htmlDescription &&
      <p className="description" dangerouslySetInnerHTML={{ __html: field.htmlDescription }}/>}

      { field.disclaimer &&
      <p className="disclaimer">
        <i className="fa fa-exclamation-triangle"/> {field.disclaimer}
      </p> }
    </div>);
};

/**
 * Custom radio button field with a label and error handling.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 *
 * @returns {void}
 */
const RadioButtonField = (field) => {
  const hasError = field.meta.touched && field.meta.invalid;
  const classes = classnames('radio form-group',
    { error: hasError }
  );
  return (
    <div className={classes}>
      <input
        id={`field-id-${field.input.name}-${field.value}`}
        name={field.input.name}
        type="radio"
        className="form-control"
        value={field.input.value}
        checked={field.value === field.input.value}
        disabled={field.disabled}
      />
      <label
        htmlFor={`field-id-${field.value}`}
        className="form-label"
        onClick={field.disabled? undefined : () => {
          field.input.onChange(field.value);
        }}
      >
        { field.label }
      </label>
      { hasError && <span className="error-msg">{field.meta.error}</span> }
    </div>);
};

/**
 * Checkbox with an input field next to it. Input becomes active when
 * the checkbox gets selected.
 * This was built specifically for the gallery feedback form so might not be reusable yet.
 *
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 *
 * @returns {void}
 */
const CheckBoxInputField = (fields) => {
  const inputClasses = classnames('text-underline', {
    empty: !fields.feedbackTextCheckbox.input.value
  });

  return (
    <div className="input-checkbox">
      <div className="checkbox">
        <input
          id={`${fields.feedback.input.name}-check`}
          type="checkbox"
          className="form-control"
          onChange={() => {
            fields.feedbackTextCheckbox.input.onChange(!fields.feedbackTextCheckbox.input.value);
          }}
          checked={fields.feedbackTextCheckbox.input.value}
        />
        <label
          htmlFor={`${fields.feedback.input.name}-check`}
          className="form-label"
        />
      </div>
      <Field
        name={fields.feedback.input.name}
        component={TextInputField}
        value={fields.values.feedback}
        classes={inputClasses}
        disabled={!fields.feedbackTextCheckbox.input.value}
      />
    </div>
  );
};

const TaggingField = (props) => {
  const {
    input, label, value, options, clearable=true, useInputValue, resetValue,
    meta: { touched, error }
  } = props;

  return (<FormGroup field={props}>
    {label && <label htmlFor={`field-id-${input.name}`}>
      <span className={classnames({'error-msg': !!error })}>{ label }</span>
    </label>}
    <div className="select-typable">
        <Select
          {...input}
          onBlur={() => input.onBlur? input.onBlur(value) : {}}
          options={options}
          value={useInputValue? input.value : value}
          simpleValue={true}
          clearable={clearable}
          searchable={true}
          resetValue={resetValue}
        />
      {touched && error && <span className="error-msg">{error}</span>}
    </div>
  </FormGroup>);
};

TaggingField.propTypes = FieldPropTypes;

/**
 * Form field wrapping <FileSelector/>.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
export class FileSelectorField extends Component {

  constructor(props) {
    super(props)

    this.handleFileSelected = this.handleFileSelected.bind(this)
    this.remove = this.remove.bind(this)
  }

  handleFileSelected(file) {
    const reader = new FileReader();
    const { withPreview } = this.props;

    reader.onloadend = () => {
      this.props.input.onChange({
        file: reader.result.split(',').pop(),
        preview: withPreview? reader.result : undefined,
        filename: file.name
      })
    }

    reader.readAsDataURL(file)
  }

  remove() {
    this.props.input.onChange(null)
  }

  render() {
    const { input: { value, name }, label } = this.props;
    const formClasses = classnames(this.props.formClasses, 'file-uploader-wrapper');

    return (
      <FormGroup field={{ ...this.props, formClasses }}>
        <div className="file-uploader-field">
          {label &&
          <label className="form-label" htmlFor={`field-id-${name}`}>{label}</label>}

          {value?
          <div className="attached">
            <i className="fa fa-trash" onClick={this.remove}/> {value.filename || value.description}
          </div>:
          <div className="form-upload-box">
            <FileSelector {...this.props} onValidFileSelected={this.handleFileSelected}/>
          </div>}
        </div>
      </FormGroup>
    );
  }
};

/**
 * Just like <FileSelectorField/>, but allows for multiple files.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
export class MultipleFileSelectorField extends Component {
  constructor(props) {
    super(props)

    this.handleFileChange = this.handleFileChange.bind(this)
  }

  handleFileChange(idx, fileValue) {
    const { input: { value, onChange } } = this.props
    const files = [...(value || [])]

    /* remove file from list... */
    if (!fileValue) {
      files.splice(idx, 1)

    /* or replace its value */
    } else {
      files[idx] = fileValue
    }

    onChange(files)
  }

  render() {
    const { input: { value, name }, meta: { touched, invalid, error },
      acceptedFileType='' } = this.props;
    const files = value || [];
    const hasError = touched && invalid;

    return (
      <div className="multiple-file-uploader-field">
        {/* existing files */}
        <div className="existing-files">
          {files.map((f, i) => (
            <FileSelectorField key={i} name={`${name}_${i}`} meta={{}}
              acceptedFileType={acceptedFileType}
              input={{value: f, onChange: (v) => this.handleFileChange(i, v)}}/>
          ))}
        </div>

        {/* new file */}
        <FileSelectorField name={`${name}_${files.length}`} meta={{}}
          acceptedFileType={acceptedFileType}
          input={{onChange: (v) => this.handleFileChange(files.length, v)}}/>

        { hasError && <span className="error-msg">{error}</span> }
      </div>
    )
  }
}

/**
 * Star rating field.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
const TEXTUAL_TYPES = {
  agreement: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
  satisfaction: ['Very Dissatisfied', 'Dissatisfied', 'Neither', 'Satisfied', 'Very Satisfied'],
  probability: ['Definitely Not', 'Probably Not', 'Might', 'Likely', 'Definitely']
}
export const StarRatingField = (props) => {
  const { input: { name, value, onChange }, label, description, textualType, min=1 } = props
  /* if not defined, `initialRate` MUST be 0 to denote a pristine field */
  const initialRate = value || 0;

  return (
    <FormGroup field={props}>
      <div className="star-rating-field">
        <label className="form-label" htmlFor={`field-id-${name}`}>{label}</label>

        {description && <p className="description">{description}</p>}

        <StarRating initialRate={initialRate} min={min} onChange={onChange}/>

        <label className="textual-rating">{TEXTUAL_TYPES[textualType][value - 1]}</label>
      </div>
    </FormGroup>
  )
}

const DATE_PICKER_DATE_FORMAT = "MM/DD/YYYY";
const DatePickerField = (field) => {
  const {
    input, onChangeAction, defaultValue, timestamp, meta: { touched, error },
  } = field;

  return (<FormGroup field={field}>
    <DatePicker
      {...omit(input, 'value')}
      onBlur={(e) => {
        if (!input.onBlur) {
          return;
        }
        return input.onBlur(
          timestamp? moment(e.target.value, DATE_PICKER_DATE_FORMAT).format() : e.target.value
        );
      }}
      dateFormat={DATE_PICKER_DATE_FORMAT}
      onChange={(e) => {
        if (onChangeAction) {
          onChangeAction(timestamp && e ? e.format() : e);
        }
        input.onChange(timestamp && e ? e.format() : e);
      }}
      selected={input.value ? moment(input.value) : null}
    />
  </FormGroup>);
};


export const ColorPickerField = (props) => {
  const { input, meta } = props;
  const hasError = meta.touched && meta.invalid;

  return (
    <FormGroup field={props}>
      <div className="color-picker-field">
        <ColorPicker value={input.value} onChange={input.onChange}/>
        { hasError && <span className="error-msg">{ meta.error }</span> }
      </div>
    </FormGroup>
  );
}

export {
  CheckBoxInputField, CheckBoxField, RadioButtonField, TextAreaField, BasicTextAreaField, ResizableTextAreaField,
  SimpleTextInputField, SelectField, TaggingField, DatePickerField,
};



// WEBPACK FOOTER //
// ./common/components/form_fields.jsx